import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Ticket, CalendarDays, Users2, Pencil, X, Flame, MessageCircle, Settings2, ShieldCheck, Star } from "lucide-react";
import { guestApi, apiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";

const STAGE_LABELS = {
  registrado: "Registrado", contactado: "Contactado", en_seguimiento: "En seguimiento",
  interesado: "Interesado", inscrito: "Inscrito", enfriado: "Enfriado", perdido: "Perdido",
};

const TA = (props) => (
  <textarea {...props} className={`w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-[#0A1C30] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 ${props.className || ""}`} />
);
const TILE_KEYS = [
  ["leadership", "Ser Supera Leadership"],
  ["world", "Supera en el Mundo"],
  ["method", "Programas y Metodología"],
  ["certifications", "Certificaciones Internacionales"],
];

export default function GuestsAdmin() {
  const [tab, setTab] = useState("contenido");
  const [c, setC] = useState(null);
  const [webinars, setWebinars] = useState([]);
  const [crm, setCrm] = useState({ prospects: [], counts: {}, stages: [] });
  const [cfg, setCfg] = useState(null);
  const [editW, setEditW] = useState(null);

  useEffect(() => { guestApi.content().then(setC).catch(() => {}); }, []);
  useEffect(() => {
    if (tab === "webinars") guestApi.adminWebinars().then(setWebinars).catch(() => {});
    if (tab === "crm") guestApi.crmProspects().then(setCrm).catch(() => {});
    if (tab === "config") guestApi.crmConfig().then(setCfg).catch(() => {});
  }, [tab]);

  const reloadCrm = async () => setCrm(await guestApi.crmProspects());
  const moveStage = async (p, stage) => { await guestApi.crmUpdate(p.id, { stage }); reloadCrm(); };
  const addNote = async (p, note) => { if (!note) return; await guestApi.crmUpdate(p.id, { note }); toast.success("Nota agregada."); reloadCrm(); };
  const saveCfg = async () => { try { await guestApi.crmSaveConfig(cfg); toast.success("Automatizaciones guardadas."); } catch (e) { toast.error(apiError(e)); } };
  const cfgArr = (k, i, field, v) => setCfg((p) => { const a = [...(p[k] || [])]; a[i] = { ...a[i], [field]: v }; return { ...p, [k]: a }; });

  const upd = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const updTile = (key, field, v) => setC((p) => ({ ...p, tiles: { ...p.tiles, [key]: { ...p.tiles[key], [field]: v } } }));
  const updArr = (k, i, v) => setC((p) => { const a = [...(p[k] || [])]; a[i] = v; return { ...p, [k]: a }; });
  const addArr = (k) => setC((p) => ({ ...p, [k]: [...(p[k] || []), ""] }));
  const delArr = (k, i) => setC((p) => ({ ...p, [k]: (p[k] || []).filter((_, x) => x !== i) }));

  const save = async () => {
    try { await guestApi.updateContent(c); toast.success("Contenido de Invitados guardado."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveWebinar = async () => {
    try {
      if (editW.id) await guestApi.updateWebinar(editW.id, editW);
      else await guestApi.createWebinar(editW);
      toast.success("Sesión guardada.");
      setEditW(null);
      setWebinars(await guestApi.adminWebinars());
    } catch (e) { toast.error(apiError(e)); }
  };
  const delWebinar = async (id) => {
    if (!window.confirm("¿Eliminar esta sesión?")) return;
    await guestApi.deleteWebinar(id); setWebinars(await guestApi.adminWebinars());
  };

  const TABS = [["contenido", "Contenido", Ticket], ["webinars", "Webinars", CalendarDays], ["crm", "SUPERA CRM", Users2], ["config", "Automatizaciones", Settings2]];

  return (
    <div data-testid="guests-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-[#0A1C30]">Invitados Especiales</h1>
          <p className="text-sm text-[#6B7688]">Embudo de captación de prospectos — 100% editable.</p>
        </div>
        {tab === "contenido" && (
          <button onClick={save} data-testid="guests-save-content" className="inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[#d8b53f] text-[#0A1C30] font-extrabold px-5 py-2.5 text-sm">
            <Save className="w-4 h-4" /> Guardar
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`guests-tab-${k}`}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition-colors ${tab === k ? "bg-[#0A1C30] text-white" : "bg-white ring-1 ring-black/5 text-[#0A1C30] hover:bg-[#FAF8F1]"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* CONTENIDO */}
      {tab === "contenido" && c && (
        <div className="space-y-6 max-w-3xl">
          <section className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
            <h2 className="font-extrabold text-[#0A1C30] mb-3">Frases de impacto (rotan al entrar)</h2>
            {(c.quotes || []).map((q, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <TA value={q} rows={2} onChange={(e) => updArr("quotes", i, e.target.value)} data-testid={`quote-${i}`} />
                <button onClick={() => delArr("quotes", i)} className="shrink-0 w-9 h-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center self-start"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => addArr("quotes")} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0A1C30] mt-1"><Plus className="w-4 h-4" /> Agregar frase</button>
            <div className="mt-4 max-w-[220px]">
              <Label className="text-xs">Segundos que dura la frase</Label>
              <Input type="number" value={c.quote_seconds || 7} onChange={(e) => upd("quote_seconds", parseInt(e.target.value || "7", 10))} />
            </div>
          </section>

          <section className="bg-white rounded-2xl ring-1 ring-black/5 p-5 space-y-3">
            <h2 className="font-extrabold text-[#0A1C30]">Portal y formulario</h2>
            <div><Label className="text-xs">Instagram URL</Label><Input value={c.instagram_url || ""} onChange={(e) => upd("instagram_url", e.target.value)} /></div>
            <div><Label className="text-xs">Título del portal</Label><Input value={c.portal?.title || ""} onChange={(e) => setC((p) => ({ ...p, portal: { ...p.portal, title: e.target.value } }))} /></div>
            <div><Label className="text-xs">Subtítulo del portal</Label><TA rows={2} value={c.portal?.subtitle || ""} onChange={(e) => setC((p) => ({ ...p, portal: { ...p.portal, subtitle: e.target.value } }))} /></div>
            <div><Label className="text-xs">Introducción del formulario de registro</Label><TA rows={2} value={c.form_intro || ""} onChange={(e) => upd("form_intro", e.target.value)} /></div>
          </section>

          <section className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
            <h2 className="font-extrabold text-[#0A1C30] mb-2">Frases de marketing (márgenes)</h2>
            {(c.marketing_phrases || []).map((q, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input value={q} onChange={(e) => updArr("marketing_phrases", i, e.target.value)} />
                <button onClick={() => delArr("marketing_phrases", i)} className="shrink-0 w-9 h-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => addArr("marketing_phrases")} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0A1C30] mt-1"><Plus className="w-4 h-4" /> Agregar frase</button>
          </section>

          {TILE_KEYS.map(([key, label]) => (
            <section key={key} className="bg-white rounded-2xl ring-1 ring-black/5 p-5 space-y-3">
              <h2 className="font-extrabold text-[#0A1C30]">Sección: {label}</h2>
              <div><Label className="text-xs">Título</Label><Input value={c.tiles?.[key]?.title || ""} onChange={(e) => updTile(key, "title", e.target.value)} /></div>
              <div><Label className="text-xs">Subtítulo</Label><Input value={c.tiles?.[key]?.subtitle || ""} onChange={(e) => updTile(key, "subtitle", e.target.value)} /></div>
              <div><Label className="text-xs">Texto</Label><TA rows={4} value={c.tiles?.[key]?.body || ""} onChange={(e) => updTile(key, "body", e.target.value)} /></div>
            </section>
          ))}

          <section className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
            <h2 className="font-extrabold text-[#0A1C30] mb-1 flex items-center gap-2"><Star className="w-4 h-4 text-[var(--gold)]" /> Contador (prueba social)</h2>
            <div className="grid grid-cols-3 gap-2">
              {(c.stats || []).map((s, i) => (
                <div key={i} className="space-y-1">
                  <Input value={s.value} onChange={(e) => setC((p) => { const a = [...p.stats]; a[i] = { ...a[i], value: e.target.value }; return { ...p, stats: a }; })} placeholder="+20,000" />
                  <Input value={s.label} onChange={(e) => setC((p) => { const a = [...p.stats]; a[i] = { ...a[i], label: e.target.value }; return { ...p, stats: a }; })} placeholder="líderes formados" />
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
            <h2 className="font-extrabold text-[#0A1C30] mb-2">Testimonios (foto/video)</h2>
            {(c.testimonials || []).map((tm, i) => (
              <div key={i} className="rounded-xl bg-[#FAF8F1] p-3 mb-3 space-y-2">
                <div className="flex gap-2">
                  <Input value={tm.name} onChange={(e) => setC((p) => { const a = [...p.testimonials]; a[i] = { ...a[i], name: e.target.value }; return { ...p, testimonials: a }; })} placeholder="Nombre" />
                  <Input value={tm.role} onChange={(e) => setC((p) => { const a = [...p.testimonials]; a[i] = { ...a[i], role: e.target.value }; return { ...p, testimonials: a }; })} placeholder="Rol (ej. Papás de Mateo)" />
                  <button onClick={() => setC((p) => ({ ...p, testimonials: p.testimonials.filter((_, x) => x !== i) }))} className="shrink-0 w-9 h-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
                <TA rows={2} value={tm.quote} onChange={(e) => setC((p) => { const a = [...p.testimonials]; a[i] = { ...a[i], quote: e.target.value }; return { ...p, testimonials: a }; })} placeholder="Testimonio" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={tm.image || ""} onChange={(e) => setC((p) => { const a = [...p.testimonials]; a[i] = { ...a[i], image: e.target.value }; return { ...p, testimonials: a }; })} placeholder="URL foto (opcional)" />
                  <Input value={tm.video_url || ""} onChange={(e) => setC((p) => { const a = [...p.testimonials]; a[i] = { ...a[i], video_url: e.target.value }; return { ...p, testimonials: a }; })} placeholder="URL video (opcional)" />
                </div>
              </div>
            ))}
            <button onClick={() => setC((p) => ({ ...p, testimonials: [...(p.testimonials || []), { name: "", role: "", quote: "", image: "", video_url: "" }] }))} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0A1C30]"><Plus className="w-4 h-4" /> Agregar testimonio</button>
          </section>
        </div>
      )}

      {/* WEBINARS */}
      {tab === "webinars" && (
        <div className="max-w-3xl">
          <button onClick={() => setEditW({ titulo: "", descripcion: "", fecha: "", hora: "", tipo: "upcoming", image: "", connect_link: "", recording_url: "", activo: true })}
            data-testid="webinar-new" className="inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[#d8b53f] text-[#0A1C30] font-extrabold px-4 py-2.5 text-sm mb-4">
            <Plus className="w-4 h-4" /> Nueva sesión
          </button>
          <div className="space-y-3">
            {webinars.map((w) => (
              <div key={w.id} className="bg-white rounded-2xl ring-1 ring-black/5 p-4 flex items-center justify-between gap-3">
                <div>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${w.tipo === "upcoming" ? "bg-[#E8F3EC] text-[#22A860]" : "bg-[#EDF0F4] text-[#6B7688]"}`}>{w.tipo === "upcoming" ? "PRÓXIMA" : "PASADA"}</span>
                  <p className="font-extrabold text-[#0A1C30] text-sm mt-1">{w.titulo}</p>
                  <p className="text-xs text-[#6B7688]">{w.fecha} {w.hora}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditW(w)} className="w-9 h-9 rounded-lg bg-[#0A1C30]/5 flex items-center justify-center"><Pencil className="w-4 h-4 text-[#0A1C30]" /></button>
                  <button onClick={() => delWebinar(w.id)} className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center"><Trash2 className="w-4 h-4 text-red-500" /></button>
                </div>
              </div>
            ))}
            {webinars.length === 0 && <p className="text-sm text-[#6B7688]">Aún no hay sesiones. Crea la primera.</p>}
          </div>
        </div>
      )}

      {/* SUPERA CRM */}
      {tab === "crm" && (
        <div className="max-w-5xl">
          <div className="flex flex-wrap gap-2 mb-4">
            {(crm.stages || []).map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-black/5 px-3 py-1.5 text-xs font-bold text-[#0A1C30]">
                {STAGE_LABELS[s]} <span className="text-[var(--gold)]">{crm.counts?.[s] || 0}</span>
              </span>
            ))}
          </div>
          <div className="bg-white rounded-2xl ring-1 ring-black/5 overflow-x-auto">
            <table className="w-full text-left min-w-[820px]">
              <thead><tr className="bg-[#FAFAFA] text-xs font-extrabold text-[#6B7688] uppercase">
                <th className="px-4 py-3">Prospecto</th><th className="px-4 py-3">Score</th><th className="px-4 py-3">Verificado</th>
                <th className="px-4 py-3">Etapa</th><th className="px-4 py-3">WhatsApp</th><th className="px-4 py-3">Nota</th>
              </tr></thead>
              <tbody>
                {(crm.prospects || []).map((p) => (
                  <tr key={p.id} data-testid={`crm-row-${p.email}`} className="border-t border-black/5 align-top">
                    <td className="px-4 py-3">
                      <p className="text-sm font-bold text-[#0A1C30] flex items-center gap-1.5">{p.nombre} {p.apellidos} {p.hot && <span title="Prospecto caliente"><Flame className="w-4 h-4 text-[#E0533D]" /></span>}</p>
                      <p className="text-xs text-[#6B7688]">{p.email}</p>
                      <p className="text-xs text-[#9AA4B4]">{p.pais} · {p.webinar_regs} webinar(s)</p>
                    </td>
                    <td className="px-4 py-3"><span className="inline-block px-2 py-1 rounded-full text-xs font-black bg-[#EAF1FF] text-[#3B7DF6]">{p.lead_score || 0}</span></td>
                    <td className="px-4 py-3 text-xs">
                      <span className={p.email_verified ? "text-[#22A860] font-bold" : "text-[#9AA4B4]"}>✉ {p.email_verified ? "Sí" : "No"}</span><br />
                      <span className={p.whatsapp_confirmed ? "text-[#22A860] font-bold" : "text-[#9AA4B4]"}>WA {p.whatsapp_confirmed ? "Sí" : "No"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <select value={p.crm_stage} onChange={(e) => moveStage(p, e.target.value)} data-testid={`crm-stage-${p.email}`} className="rounded-lg border border-black/10 text-xs px-2 py-1.5 font-bold text-[#0A1C30]">
                        {(crm.stages || []).map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      {p.whatsapp ? (
                        <a href={`https://wa.me/${(p.whatsapp || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hola ${p.nombre}, te saludamos de Supera Leadership 👋`)}`} target="_blank" rel="noreferrer"
                          data-testid={`crm-wa-${p.email}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-white font-bold px-3 py-1.5 text-xs">
                          <MessageCircle className="w-3.5 h-3.5" /> Escribir
                        </a>
                      ) : <span className="text-xs text-[#9AA4B4]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <NoteInput onAdd={(v) => addNote(p, v)} />
                      {(p.crm_notes || []).slice(-2).map((n, i) => <p key={i} className="text-[11px] text-[#6B7688] mt-1">• {n.text}</p>)}
                    </td>
                  </tr>
                ))}
                {(!crm.prospects || crm.prospects.length === 0) && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6B7688] text-sm">Aún no hay prospectos.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AUTOMATIZACIONES */}
      {tab === "config" && cfg && (
        <div className="max-w-3xl space-y-6">
          <div className="flex justify-end"><button onClick={saveCfg} data-testid="crm-config-save" className="inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[#d8b53f] text-[#0A1C30] font-extrabold px-5 py-2.5 text-sm"><Save className="w-4 h-4" /> Guardar</button></div>
          <section className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
            <h2 className="font-extrabold text-[#0A1C30] mb-1">Seguimiento automático (correo)</h2>
            <p className="text-xs text-[#6B7688] mb-3">Se envía automáticamente al prospecto si no se inscribe. Usa {"{nombre}"}.</p>
            {(cfg.followups || []).map((f, i) => (
              <div key={i} className="rounded-xl bg-[#FAF8F1] p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Label className="text-xs">A las</Label>
                  <Input type="number" value={f.offset_hours} onChange={(e) => cfgArr("followups", i, "offset_hours", parseInt(e.target.value || "0", 10))} className="w-24 h-9" /> <span className="text-xs">horas</span>
                  <button onClick={() => setCfg((p) => ({ ...p, followups: p.followups.filter((_, x) => x !== i) }))} className="ml-auto w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
                <Input value={f.subject} onChange={(e) => cfgArr("followups", i, "subject", e.target.value)} placeholder="Asunto" className="mb-2" />
                <TA rows={2} value={f.message} onChange={(e) => cfgArr("followups", i, "message", e.target.value)} placeholder="Mensaje" />
              </div>
            ))}
            <button onClick={() => setCfg((p) => ({ ...p, followups: [...(p.followups || []), { offset_hours: 24, channel: "email", subject: "", message: "" }] }))} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0A1C30]"><Plus className="w-4 h-4" /> Agregar seguimiento</button>
          </section>
          <section className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
            <h2 className="font-extrabold text-[#0A1C30] mb-1">Recordatorios de webinar (push + correo)</h2>
            <p className="text-xs text-[#6B7688] mb-3">Se envía a los inscritos antes de cada sesión. Usa {"{titulo}"}.</p>
            {(cfg.webinar_reminders || []).map((r, i) => (
              <div key={i} className="flex gap-2 items-start mb-2">
                <div className="w-32 shrink-0"><Input type="number" value={r.offset_minutes} onChange={(e) => cfgArr("webinar_reminders", i, "offset_minutes", parseInt(e.target.value || "0", 10))} /><span className="text-[10px] text-[#6B7688]">minutos antes</span></div>
                <TA rows={2} value={r.message} onChange={(e) => cfgArr("webinar_reminders", i, "message", e.target.value)} />
                <button onClick={() => setCfg((p) => ({ ...p, webinar_reminders: p.webinar_reminders.filter((_, x) => x !== i) }))} className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setCfg((p) => ({ ...p, webinar_reminders: [...(p.webinar_reminders || []), { offset_minutes: 60, message: "En 1 hora: {titulo}" }] }))} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0A1C30]"><Plus className="w-4 h-4" /> Agregar recordatorio</button>
          </section>
        </div>
      )}

      {/* Editor de webinar */}
      {editW && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setEditW(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h3 className="font-black text-lg text-[#0A1C30]">{editW.id ? "Editar" : "Nueva"} sesión</h3><button onClick={() => setEditW(null)}><X className="w-5 h-5 text-[#6B7688]" /></button></div>
            <div><Label className="text-xs">Título</Label><Input value={editW.titulo} onChange={(e) => setEditW({ ...editW, titulo: e.target.value })} data-testid="webinar-titulo" /></div>
            <div><Label className="text-xs">Descripción</Label><TA rows={2} value={editW.descripcion} onChange={(e) => setEditW({ ...editW, descripcion: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Fecha (AAAA-MM-DD)</Label><Input value={editW.fecha} onChange={(e) => setEditW({ ...editW, fecha: e.target.value })} placeholder="2026-08-15" data-testid="webinar-fecha" /></div>
              <div><Label className="text-xs">Hora</Label><Input value={editW.hora} onChange={(e) => setEditW({ ...editW, hora: e.target.value })} placeholder="19:00" data-testid="webinar-hora" /></div>
            </div>
            <div><Label className="text-xs">Tipo</Label>
              <select value={editW.tipo} onChange={(e) => setEditW({ ...editW, tipo: e.target.value })} className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-sm">
                <option value="upcoming">Próxima (en vivo)</option><option value="past">Pasada (grabación)</option>
              </select>
            </div>
            <div><Label className="text-xs">Imagen (URL)</Label><Input value={editW.image} onChange={(e) => setEditW({ ...editW, image: e.target.value })} /></div>
            <div><Label className="text-xs">Cupos (0 = ilimitado)</Label><Input type="number" value={editW.cupos || 0} onChange={(e) => setEditW({ ...editW, cupos: parseInt(e.target.value || "0", 10) })} data-testid="webinar-cupos" /></div>
            <div><Label className="text-xs">Enlace grabación (solo pasadas)</Label><Input value={editW.recording_url} onChange={(e) => setEditW({ ...editW, recording_url: e.target.value })} /></div>
            <div className="flex items-center gap-2"><Switch checked={editW.activo} onCheckedChange={(v) => setEditW({ ...editW, activo: v })} /><span className="text-sm">Visible para invitados</span></div>
            <button onClick={saveWebinar} data-testid="webinar-save" className="w-full rounded-xl bg-[var(--gold)] hover:bg-[#d8b53f] text-[#0A1C30] font-extrabold py-3 text-sm">Guardar sesión</button>
          </div>
        </div>
      )}
    </div>
  );
}

function NoteInput({ onAdd }) {
  const [v, setV] = React.useState("");
  return (
    <div className="flex gap-1">
      <input value={v} onChange={(e) => setV(e.target.value)} placeholder="Nota…" className="w-32 rounded-lg border border-black/10 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40" />
      <button onClick={() => { onAdd(v); setV(""); }} className="shrink-0 w-7 h-7 rounded-lg bg-[#0A1C30] text-white flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
    </div>
  );
}
